import {
  useLoaderData,
  Link as RemixLink,
  useParams,
  useSearchParams,
  useRouteError,
  isRouteErrorResponse,
} from "@remix-run/react";

import { parseISO } from "date-fns";
import { db } from "#app/utils/db.server.js";
import { createUserSession, getUser } from "#app/utils/session.server.js";
import {
  User,
  AvailabilityRequest,
  AvailabilityRequestForm,
} from "#app/models/index.js";
import { validatePublicPost } from "#app/utils/security.server.js";
import { captureAvailabilityRequestFormDiagnostics } from "#app/utils/form.server.js";
import { notFound, badRequest } from "#app/utils/response.server.js";
import { affectedAgencyRedirectResponse } from "#app/utils/affectedAgency.server.js";
import { sendEmail, InactiveRecipientsError } from "#app/utils/email.server.js";
import { generateAccessToken } from "#app/utils/accessToken.server.js";
import { kickoffScanningFireAndForget } from "#app/utils/kickoffScanning.server.js";
import { parseLocaleFromHeader } from "#app/utils/resolveLocale.js";
import { sanitizeSites } from "#app/utils/sanitize.server.js";
import { filterToFacilitySites } from "#app/utils/filterToFacilitySites.server.js";
import {
  getActiveOfferForFacility,
  getOfferCopy,
  isFreeAccessFacility,
} from "#app/config/freeAccessOffers.js";
import {
  parseDeepLinkSelectedSiteIds,
  parseDeepLinkMinStayLength,
} from "#app/utils/parseDeepLinkParams.js";
import SendFounderIntroEmail from "#jobs/SendFounderIntroEmail.js";
import { COUNTRIES } from "#app/config/countries.js";
import { getFacilityPresets } from "#app/config/tripPlannerPresets.js";
import { getDefaultTripNights } from "#app/utils/tripPlannerDefaults.js";
import { getPostHog } from "#app/utils/posthog.server.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";

import { Text, Icon, Button, Stack, Link, Code, Box } from "@chakra-ui/react";
import { MdOutlineArrowBackIosNew as BackIcon } from "react-icons/md";
import {
  List,
  ListItem,
  ListItemHeader,
} from "#app/components/layouts/FormList.jsx";
import { Layout, Header, Content } from "#app/components/layouts/AppPage.jsx";
import { RequestForm, ErrorMessage } from "#app/components/index.jsx";
import { GoLiveDate } from "#app/components/experimental/GoLiveDate.jsx";
import { getFilteredSiteIds } from "#app/components/RequestForm/SelectedSitesInput.jsx";
import { useHelpTextComponent } from "#app/components/RequestForm/HelpText.jsx";
import { useOptionalUser } from "#app/hooks/useUser.js";

import {
  includeNightsText,
  arrivalText,
  nightsText,
  sitesText,
  tripSummaryText,
  matchStyleText,
} from "#app/utils/availabilityRequest.js";

export function meta({ data, matches }) {
  const baseUrl = getBaseUrl(matches);

  if (!data) {
    return getSeoMeta({
      title: "Campground not found | Create a Request | Schnerp",
      description: "Please use the searchbar to find an active facility",
      image: getDefaultOgImage(baseUrl),
    });
  }

  return getSeoMeta({
    title: `${data.facility.name} | Create a Request | Schnerp`,
    description: `Tell us your availability window and site preferences for ${data.facility.name} on ${data.facility.agency.name}. We'll notify you if a spot opens up.`,
    url: `${baseUrl}/new/${data.facility.slug}`,
    image: getDefaultOgImage(baseUrl),
  });
}

export const handle = {
  breadcrumb: () => {
    return {
      to: `/new`,
      text: "Create a request",
    };
  },
};

export async function loader({ params, request }) {
  const user = await getUser(request);
  const locale =
    user?.locale ||
    parseLocaleFromHeader(request.headers.get("Accept-Language"));

  const facility = await db.facility.findUnique({
    where: {
      slug: params.facility,
    },
    include: {
      sites: true,
      agency: {
        select: {
          name: true,
          scanner: true,
          country: true,
        },
      },
    },
  });

  if (!facility) {
    throw notFound({
      error: "We can't find this facility",
    });
  }

  // Affected-agency redirect: Alberta/SK/PEI Parks were taken offline in August
  // 2025 after Aspira asked Schnerp to stop scanning. Without this redirect,
  // direct URLs to their /new/<facility> pages would load a request form for
  // facilities Schnerp no longer monitors (see design doc: Alberta Parks
  // ongoing comms). Route them to the canonical /alberta-parks explainer page
  // instead.
  const redirectResponse = affectedAgencyRedirectResponse(
    params.facility,
    facility.agency.scanner,
  );
  if (redirectResponse) throw redirectResponse;

  const tripPlannerPresets = facility.supportsTripPlanner
    ? getFacilityPresets(facility.agency.scanner, facility.slug)
    : null;

  // Child backcountry facilities always have exactly 1 site. Only run the
  // parent lookup in that case — skips the query for the vast majority of
  // facility page loads.
  let tripPlannerParent = null;
  if (!facility.supportsTripPlanner && facility.sites.length === 1) {
    const candidates = await db.facility.findMany({
      where: {
        supportsTripPlanner: true,
        id: { not: facility.id },
        sites: { some: { id: facility.sites[0].id } },
      },
      select: {
        name: true,
        slug: true,
        _count: { select: { sites: true } },
      },
      orderBy: { sites: { _count: "asc" } },
      take: 1,
    });
    tripPlannerParent = candidates[0] || null;
  }

  sanitizeSites(facility.sites);

  // Same condition verifyUserForRequestForm enforces at submit, exposed at
  // view time so RequestForm can warn before the user fills out the form.
  // Free-access facilities don't consume a cap slot, so they never trip it.
  const atRequestLimit =
    user && !isFreeAccessFacility(params.facility)
      ? await AvailabilityRequest.isLimitReached(user)
      : false;

  const data = {
    facility,
    tripPlannerPresets,
    tripPlannerParent,
    locale,
    atRequestLimit,
    ...AvailabilityRequestForm.getLoaderData(facility, { locale }),
  };

  return data;
}

// TODO: Remove duplicate code from /r/$rId/clone.jsx
export async function action({ request, params }) {
  const form = await validatePublicPost(request);

  // Validate facility + R8 guard BEFORE any writes.
  //
  // Order matters here: running `verifyUserForRequestForm` first (which
  // upserts a User row and persists attribution side-effects) before the
  // R8 check means a tampered submit leaves an orphan User in the DB and
  // unfairly credits the referrer for a request we immediately reject.
  // Hoist the cheap validations (facility lookup + cross-facility site
  // filter) so the User is only touched on a submit that will actually
  // succeed.
  const facilityForGuard = await db.facility.findUnique({
    where: { slug: params.facility },
    select: {
      id: true,
      sites: { select: { id: true } },
      agency: { select: { scanner: true } },
    },
  });
  if (!facilityForGuard) {
    throw notFound({ error: "We can't find this facility" });
  }

  // Pre-parse just the site ids so we can run R8 without needing a userId.
  // The full parseForm happens below after auth; this is a cheap side-
  // effect-free extraction.
  const { selectedSiteIds: submittedIds } =
    AvailabilityRequestForm.parseFormSitesAndDates(form);

  const { validIds } = filterToFacilitySites({
    submittedIds,
    facilitySiteIds: facilityForGuard.sites.map((s) => s.id),
    facilitySlug: params.facility,
  });
  if (validIds.length === 0) {
    return badRequest({
      fieldErrors: [
        {
          fieldName: "selectedSiteIds",
          message: "Please select at least one site for this facility.",
        },
      ],
    });
  }

  // Validated. Now upsert the User — attribution side-effects only land on
  // submits that will actually produce an AvailabilityRequest.
  const {
    email,
    timeZone,
    locale,
    referralId,
    couponId,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    referrer,
    firstSeenAt,
  } = form;

  const user = await User.verifyUserForRequestForm({
    request,
    email,
    timeZone,
    locale,
    referralId,
    couponId,
    facilitySlug: params.facility,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    referrer,
    firstSeenAt,
  });

  // This is a weird pattern -- it distinguishes between
  // a response that the fetcher can handle (returned) and
  // a response useCatch should handle (thrown)
  if (user instanceof Response) {
    return user;
  }

  const data = AvailabilityRequestForm.parseForm({ form, userId: user.id });

  if (data instanceof Response) {
    return data;
  }

  const minCapacityError = AvailabilityRequestForm.validateMinCapacity({
    minCapacity: data.minCapacity,
    agencyScanner: facilityForGuard.agency.scanner,
  });

  if (minCapacityError) {
    return minCapacityError;
  }

  const tripNightsError = AvailabilityRequestForm.validateTripNights({
    isTripPlanner: data.isTripPlanner,
    tripNights: data.tripNights,
    selectedSiteIds: validIds,
  });

  if (tripNightsError) {
    return tripNightsError;
  }

  // Lock the facility binding to the URL slug. parseForm pulls
  // data.facility.connect.id from the attacker-controlled form.facilityId
  // hidden field; the URL slug is the authoritative identity.
  data.facility = { connect: { id: facilityForGuard.id } };
  data.selectedSiteIds = validIds;
  data.sites = { connect: validIds.map((id) => ({ id })) };

  captureAvailabilityRequestFormDiagnostics(form, request);

  const availabilityRequest = await db.availabilityRequest.create({
    data,
    include: {
      // _count: {
      //   select: {
      //     sites: true,
      //   },
      // },
      user: {
        include: {
          subscription: true,
        },
      },
      facility: { include: { agency: true } },
    },
  });

  await AvailabilityRequest.reconcileCheckedState(availabilityRequest.id);

  availabilityRequest._count = {
    sites: availabilityRequest.selectedSiteIds.length,
  };

  await kickoffScanningFireAndForget(availabilityRequest);

  const freeAccessOffer = getActiveOfferForFacility(
    availabilityRequest.facility,
  );
  const freeAccessCopy = getOfferCopy(freeAccessOffer);

  try {
    const isTrialExpired =
      user.subscription.status === "trial" &&
      user.subscription.trialNotificationsLeft === 0;

    const locals = {
      uuid: availabilityRequest.uuid,
      // Access token for the email's /r/{uuid}?t={accessToken} link: a
      // magic login that lands on this request (Pattern B in
      // docs/architecture/email-link-auth.md).
      accessToken: generateAccessToken(user.id, "email"),
      facilityName: availabilityRequest.facility.name,
      facilitySubName: availabilityRequest.facility.subName,
      arrivalText: arrivalText(availabilityRequest, { locale }),
      nightsText:
        includeNightsText(availabilityRequest) &&
        !availabilityRequest.isTripPlanner
          ? nightsText(availabilityRequest)
          : undefined,
      sitesText: sitesText(availabilityRequest, {
        includeSiteType: true,
        country: availabilityRequest.facility.agency.country,
        locale,
      }),
      isTripPlanner: availabilityRequest.isTripPlanner,
      isFreeAccess: !!freeAccessCopy,
      freeAccessHeadline: freeAccessCopy?.headline ?? null,
      freeAccessBody: freeAccessCopy?.body ?? null,
      trialNotificationsLeft: Math.max(
        0,
        user.subscription.trialNotificationsLeft,
      ),
    };

    if (availabilityRequest.isTripPlanner && availabilityRequest.tripNights) {
      locals.tripSummaryText = tripSummaryText(availabilityRequest);
      locals.matchStyleText = matchStyleText(availabilityRequest);
    }

    if (isTrialExpired && !freeAccessOffer) {
      await sendEmail({
        template: "new_request_expired_trial",
        to: availabilityRequest.user.email,
        locals,
      });
    } else {
      await sendEmail({
        template: "new_request",
        to: availabilityRequest.user.email,
        locals,
      });
    }
  } catch (e) {
    if (e instanceof InactiveRecipientsError) {
      return badRequest({
        fieldErrors: [
          {
            fieldName: "email",
            message:
              "This email address has been marked as inactive. Please double check your email, or reach out to support at help@schnerp.com to re-activate it.",
          },
        ],
      });
    }
    throw e;
  }

  // Enqueue founder intro email for new NZ/AU users (30min delay)
  const scanner = availabilityRequest.facility.agency.scanner;
  const founderIntroScanners = [
    ...COUNTRIES.nz.scanners,
    ...COUNTRIES.au.scanners,
  ];
  if (!user.founderIntroSentAt && founderIntroScanners.includes(scanner)) {
    SendFounderIntroEmail.performLater({ userId: user.id });
  }

  // The only way I can figure out how to make the `createUserSession`
  // load the flash message correctly is to put a query param at the end of an un-redirected page
  //
  // i think the issue is that without the query param, the root loader (which has the flashMessage)
  // does not reload it's data (even though i think it should?)
  // the docs mention that adding a new query param forces all the loaders to reload
  // the redirected constraint came from some trial and error
  getPostHog().capture({
    distinctId: user.id.toString(),
    event: "availability_request_created",
    properties: {
      facility_name: availabilityRequest.facility.name,
      facility_slug: params.facility,
      agency: availabilityRequest.facility.agency.scanner,
      country: availabilityRequest.facility.agency.country,
      is_trip_planner: availabilityRequest.isTripPlanner,
      site_count: availabilityRequest.selectedSiteIds.length,
    },
  });

  return createUserSession(
    request,
    user.id,
    `/r/${availabilityRequest.uuid}?new=true`,
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  const params = useParams();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <ErrorMessage
        status="warning"
        title="Campground not found"
        description={
          <>
            <Code my={4}>{params.facility}</Code>
            <Text>
              This campground may have been removed from our system, or be
              temporarily unavailable.
            </Text>
            <Button
              leftIcon={<Icon as={BackIcon} />}
              as={RemixLink}
              to="/new"
              my={6}
            >
              Find a campground
            </Button>
          </>
        }
      />
    );
  }

  throw error;
};

export default function NewRequest() {
  const data = useLoaderData();
  const [searchParams] = useSearchParams();
  const { siteTypes, facility, tripPlannerPresets, tripPlannerParent, locale } =
    data;

  const aliasType = searchParams.get("aliasType");
  const aliasLabel = searchParams.get("aliasLabel");
  const datesParam = searchParams.get("dates");
  const selectedSiteIdsParam = searchParams.get("selectedSiteIds");
  const siteTypeParam = searchParams.get("siteType");
  const minStayLengthParam = searchParams.get("minStayLength");

  let filteredSiteIds = [];
  let siteType = null;
  const accessibilityPreference = "exclude";

  if (siteTypes.length === 1) {
    siteType = siteTypes[0].value;
    filteredSiteIds = getFilteredSiteIds({
      facility,
      siteType,
      accessibilityPreference,
    });
  } else if (siteTypeParam) {
    // Public deep links may preselect `?siteType=rv|tent|lodging|...`.
    // Only honor values the form actually exposes (siteTypes[].value), so
    // stale or invented values degrade to an unselected form.
    const matched = siteTypes.find((opt) => opt.value === siteTypeParam);
    if (matched) {
      siteType = matched.value;
      filteredSiteIds = getFilteredSiteIds({
        facility,
        siteType,
        accessibilityPreference,
      });
    }
  }

  // Strict ISO-date gate: parseISO accepts time components and ISO weeks,
  // but /api/v1/stay only ever emits YYYY-MM-DD and the form's day math
  // assumes the same. Reject anything else so partner-crafted TZ-shifted
  // strings can't slip in via the `dates=` param.
  const prefillDates = datesParam
    ? datesParam
        .split(",")
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
        .map((d) => parseISO(d))
        .filter((d) => !isNaN(d.getTime()))
    : [];

  // Pre-seed from `/api/v1/stay` deep link. The in-facility intersection is
  // a UX convenience so stale deep links degrade gracefully; the action-layer
  // guard (R8, filterToFacilitySites) is the authoritative check.
  const prefillSelectedSiteIds = parseDeepLinkSelectedSiteIds(
    selectedSiteIdsParam,
    facility.sites.map((s) => s.id),
  );
  const prefillMinStayLength = parseDeepLinkMinStayLength(minStayLengthParam);

  const defaultValues = {
    timeZone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
    locale,
    selectedDates: prefillDates,
    minStayLength: prefillMinStayLength ?? 1,
    siteType,
    selectedFilters: [],
    doubleSitePreference: "any",
    accessibilityPreference,
    maxRVLength: "",
    maxElectric: "",
    selectedSiteIds:
      prefillSelectedSiteIds?.map(String) ?? filteredSiteIds.slice(),
    filteredSiteIds,
    minCapacity: 1,
    emailEnabled: true,
    smsEnabled: true,
    whatsappEnabled: true,
    pushEnabled: true,
    // Trip planner
    isTripPlanner: !!facility.supportsTripPlanner,
    tripNights: getDefaultTripNights(tripPlannerPresets, facility, {
      aliasType,
      aliasLabel,
      prefillSelectedSiteIds,
      prefillMinStayLength,
    }),
    // Watching a trip most naturally means the whole trip; single-night
    // alerts are an opt-in once the form has spelled out what they mean.
    allowPartialMatch: facility.supportsTripPlanner ? false : null,
  };

  const HelpTextComponent = useHelpTextComponent({ facility });

  return (
    <Layout>
      <Header
        title={`${facility.name}`}
        subtitle={
          <>
            {facility.subName}
            <GoLiveDate site={facility.sites[0]} as="span" display="block" />
          </>
        }
        subtitleProps={{ color: "muted" }}
        action={
          <Button
            variant="secondary"
            leftIcon={<Icon as={BackIcon} />}
            as={RemixLink}
            to="/new"
          >
            Back to search
          </Button>
        }
      />
      <Content>
        {!!HelpTextComponent && (
          <List>
            <ListItem>
              <ListItemHeader
                title={HelpTextComponent}
                titleProps={{
                  fontSize: "md",
                  fontWeight: "regular",
                  w: "100%",
                  as: Box,
                }}
              />
            </ListItem>

            {/* Blank stack is required because we have to start a brand new one in RequestForm */}
            <Stack />
          </List>
        )}

        <RequestForm
          key={`${facility.slug}-${aliasType || ""}-${aliasLabel || ""}-${datesParam || ""}-${selectedSiteIdsParam || ""}-${siteTypeParam || ""}-${minStayLengthParam || ""}`}
          mode="create"
          defaultValues={defaultValues}
          {...data}
          renderSubmit={(fetcher) => {
            return (
              <Button
                variant="primary"
                type="submit"
                w="100%"
                isLoading={
                  fetcher.state === "submitting" || fetcher.state === "loading"
                }
              >
                Create new request
              </Button>
            );
          }}
        />
      </Content>
    </Layout>
  );
}
